import { MetricsV1AggregationType } from '@spotify/confidence-graphql';

import { aggregationUtils } from '../../Aggregation';
import { RatioMeasurement, RatioMetricSpec } from './measurementInput';
import {
  applyCap,
  applyThreshold,
  coalesceToZeroIf,
  filterToSqlPredicate,
  filterToWhereClause,
  quoteIdentifier,
} from './sqlHelpers';

export type RatioSnippetArgs = {
  entity?: string | null;
  factTable?: string | null;
  measurement: RatioMeasurement;
};

const buildRatioExprs = (
  spec: RatioMetricSpec,
): { numerator: string; denominator: string } => {
  const numerator = spec.numerator?.name || '*';
  const denominator = spec.denominator?.name || '*';
  const numAgg = aggregationUtils.getAggregationTypeSqlLabel(
    spec.numeratorAggregation?.type ??
      MetricsV1AggregationType.AggregationTypeAvg,
  );
  const denomAgg = aggregationUtils.getAggregationTypeSqlLabel(
    spec.denominatorAggregation?.type ??
      MetricsV1AggregationType.AggregationTypeAvg,
  );
  const numeratorPredicate = filterToSqlPredicate(spec.numeratorFilter);
  const caseValue = numerator === '*' ? '1' : numerator;
  const numExpr = numeratorPredicate
    ? `${numAgg}(CASE WHEN ${numeratorPredicate} THEN ${caseValue} END)`
    : `${numAgg}(${numerator})`;
  return {
    numerator: applyCap(
      applyThreshold(numExpr, spec.numeratorAggregation?.threshold),
      spec.numeratorAggregation?.cap,
    ),
    denominator: applyCap(
      applyThreshold(
        `${denomAgg}(${denominator})`,
        spec.denominatorAggregation?.threshold,
      ),
      spec.denominatorAggregation?.cap,
    ),
  };
};

export const ratioSnippet = ({
  entity,
  factTable,
  measurement,
}: RatioSnippetArgs): string => {
  const entityCol = quoteIdentifier(entity || 'entity');
  const factTableName = quoteIdentifier(factTable || 'fact_table');
  const replaceMissingWithZero =
    measurement.nullHandling?.replaceEntityNullWithZero ?? false;
  const exprs = buildRatioExprs(measurement.typeSpec.ratioMetricSpec);

  return `WITH measurements AS (
  SELECT
    ${entityCol},
    ${exprs.numerator} as numerator,
    ${exprs.denominator} as denominator
  FROM ${factTableName}${filterToWhereClause(measurement.filter)}
  GROUP BY ${entityCol}
)

SELECT
  SUM(${coalesceToZeroIf('numerator', replaceMissingWithZero)}) / SUM(${coalesceToZeroIf('denominator', replaceMissingWithZero)}) as metric
FROM measurements`;
};
